import { Loading } from '@components/loading';
import { IUser } from '@components/user/IUser';
import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useState
} from 'react';
import { useAsync } from 'react-async';
import {
  ILoginRequest,
  requestLogin,
  requestLogout,
  requestUser
} from './auth-service';

export interface IAuthContextProps {
  data: IUser | undefined;
  loading: boolean;
  error: Error | undefined;
  login: (data: ILoginRequest) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<IAuthContextProps | undefined>(undefined);

async function bootstrapAppData() {
  const user = await requestUser();
  return user;
}

const AuthProvider: React.FC<{ value?: Partial<IAuthContextProps> }> = ({
  value,
  ...rest
}) => {
  const [loading, setLoading] = useState(false);
  const [firstAttemptFinished, setFirstAttemptFinished] = useState(false);

  const { data, isPending, isSettled, error, setError, reload } = useAsync({
    promiseFn: bootstrapAppData
  });

  useLayoutEffect(() => {
    if (isSettled) {
      setFirstAttemptFinished(true);
    }
  }, [isSettled]);

  if (!firstAttemptFinished) {
    if (isPending) {
      return <Loading />;
    }
  }

  function handleLogin(data: ILoginRequest) {
    setLoading(true);
    requestLogin(data)
      .then(reload)
      .catch((response: Error) => setError(response))
      .finally(() => setLoading(false));
  }

  const login = (data: ILoginRequest) => handleLogin(data);
  const logout = () => requestLogout().then(reload);

  return (
    <AuthContext.Provider
      value={
        { data, login, logout, loading, error, ...value } as IAuthContextProps
      }
      {...rest}
    />
  );
};

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
}

export { AuthProvider, useAuth };
